// Archive 组件 - 博客归档页面
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './index.css';

const Archive = () => {
  const [posts, setPosts] = useState([]);
  const [groupedPosts, setGroupedPosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('time'); // time or category
  const [selectedYear, setSelectedYear] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // 模拟获取博客文章数据
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // 模拟博客文章数据
        const mockPosts = [
          {
            id: 1,
            title: "我的React学习之旅",
            date: "2024-01-15",
            category: "前端开发",
            tags: ["React", "JavaScript"]
          },
          {
            id: 2,
            title: "深入理解JavaScript闭包",
            date: "2024-01-10",
            category: "编程基础",
            tags: ["JavaScript", "闭包"]
          },
          {
            id: 3,
            title: "CSS Grid布局完全指南",
            date: "2024-01-05",
            category: "前端开发",
            tags: ["CSS", "布局"]
          },
          {
            id: 4,
            title: "Node.js实战：构建RESTful API",
            date: "2023-12-28",
            category: "后端开发",
            tags: ["Node.js", "API"]
          },
          {
            id: 5,
            title: "Vue 3 Composition API详解",
            date: "2023-12-20",
            category: "前端开发",
            tags: ["Vue", "框架"]
          },
          {
            id: 6,
            title: "Webpack 5从入门到精通",
            date: "2023-12-15",
            category: "工具链",
            tags: ["Webpack", "构建工具"]
          },
          {
            id: 7,
            title: "TypeScript进阶技巧",
            date: "2023-12-10",
            category: "编程基础",
            tags: ["TypeScript", "JavaScript"]
          },
          {
            id: 8,
            title: "响应式设计最佳实践",
            date: "2023-11-25",
            category: "前端开发",
            tags: ["CSS", "响应式"]
          },
          {
            id: 9,
            title: "数据库设计原则",
            date: "2023-11-15",
            category: "后端开发",
            tags: ["数据库", "设计"]
          },
          {
            id: 10,
            title: "Docker容器化部署指南",
            date: "2023-11-05",
            category: "运维部署",
            tags: ["Docker", "部署"]
          },
          {
            id: 11,
            title: "Git工作流详解",
            date: "2023-10-20",
            category: "工具链",
            tags: ["Git", "版本控制"]
          },
          {
            id: 12,
            title: "微服务架构设计",
            date: "2023-10-10",
            category: "后端开发",
            tags: ["微服务", "架构"]
          }
        ];
        
        setPosts(mockPosts);
        groupPostsByTime(mockPosts);
        setLoading(false);
      } catch (err) {
        console.error("加载文章失败:", err);
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // 按时间分组文章
  const groupPostsByTime = (postsData) => {
    const grouped = {};
    
    postsData.forEach(post => {
      const year = post.date.split('-')[0];
      const month = new Date(post.date).toLocaleString('zh-CN', { month: 'long' });
      
      if (!grouped[year]) {
        grouped[year] = {};
      }
      
      if (!grouped[year][month]) {
        grouped[year][month] = [];
      }
      
      grouped[year][month].push(post);
    });
    
    setGroupedPosts(grouped);
  };

  // 按分类分组文章
  const groupPostsByCategory = (postsData) => {
    const grouped = {};
    
    postsData.forEach(post => {
      const category = post.category;
      
      if (!grouped[category]) {
        grouped[category] = [];
      }
      
      grouped[category].push(post);
    });
    
    setGroupedPosts(grouped);
  };

  // 切换视图模式
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'time') {
      groupPostsByTime(posts);
    } else {
      groupPostsByCategory(posts);
    }
    setSelectedYear('all');
    setSelectedCategory('all');
  };

  // 获取年份列表
  const getYears = () => {
    return Object.keys(groupedPosts).sort((a, b) => b - a);
  };

  // 获取分类列表
  const getCategories = () => {
    return Object.keys(groupedPosts);
  };

  // 过滤文章
  const getFilteredPosts = () => {
    if (viewMode === 'time') {
      if (selectedYear === 'all') {
        return groupedPosts;
      } else {
        const filtered = {};
        Object.keys(groupedPosts[selectedYear] || {}).forEach(month => {
          filtered[month] = groupedPosts[selectedYear][month];
        });
        return { [selectedYear]: filtered };
      }
    } else {
      if (selectedCategory === 'all') {
        return groupedPosts;
      } else {
        return { [selectedCategory]: groupedPosts[selectedCategory] };
      }
    }
  };

  if (loading) {
    return (
      <div className="archive-container">
        <div className="loading">正在加载归档内容...</div>
      </div>
    );
  }

  const filteredPosts = getFilteredPosts();

  return (
    <div className="archive-container">
      <div className="archive-header">
        <h1>文章归档</h1>
        <p>浏览所有历史文章</p>
      </div>

      <div className="archive-controls">
        <div className="view-mode-toggle">
          <button 
            className={viewMode === 'time' ? 'active' : ''}
            onClick={() => toggleViewMode('time')}
          >
            按时间归档
          </button>
          <button 
            className={viewMode === 'category' ? 'active' : ''}
            onClick={() => toggleViewMode('category')}
          >
            按分类归档
          </button>
        </div>

        {viewMode === 'time' ? (
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(e.target.value)}
            className="filter-select"
          >
            <option value="all">所有年份</option>
            {getYears().map(year => (
              <option key={year} value={year}>{year}年</option>
            ))}
          </select>
        ) : (
          <select 
            value={selectedCategory} 
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">所有分类</option>
            {getCategories().map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        )}
      </div>

      <div className="archive-content">
        {viewMode === 'time' ? (
          Object.keys(filteredPosts).length > 0 ? (
            Object.keys(filteredPosts)
              .sort((a, b) => b - a)
              .map(year => (
                <div key={year} className="archive-year">
                  <h2 className="year-header">{year}年</h2>
                  {Object.keys(filteredPosts[year])
                    .sort((a, b) => {
                      const monthOrder = [
                        '一月', '二月', '三月', '四月', '五月', '六月',
                        '七月', '八月', '九月', '十月', '十一月', '十二月'
                      ];
                      return monthOrder.indexOf(a) - monthOrder.indexOf(b);
                    })
                    .map(month => (
                      <div key={month} className="archive-month">
                        <h3 className="month-header">{month}</h3>
                        <ul className="posts-list">
                          {filteredPosts[year][month]
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map(post => (
                              <li key={post.id} className="post-item">
                                <Link to={`/post/${post.id}`} className="post-link">
                                  <span className="post-date">
                                    {new Date(post.date).getDate()}日
                                  </span>
                                  <span className="post-title">{post.title}</span>
                                  <span className="post-category">{post.category}</span>
                                </Link>
                              </li>
                            ))
                          }
                        </ul>
                      </div>
                    ))
                  }
                </div>
              ))
          ) : (
            <div className="no-posts">暂无文章</div>
          )
        ) : (
          Object.keys(filteredPosts).length > 0 ? (
            Object.keys(filteredPosts)
              .map(category => (
                <div key={category} className="archive-category">
                  <h2 className="category-header">{category}</h2>
                  <ul className="posts-list">
                    {filteredPosts[category]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map(post => (
                        <li key={post.id} className="post-item">
                          <Link to={`/post/${post.id}`} className="post-link">
                            <span className="post-date">{post.date}</span>
                            <span className="post-title">{post.title}</span>
                          </Link>
                        </li>
                      ))
                    }
                  </ul>
                </div>
              ))
          ) : (
            <div className="no-posts">暂无文章</div>
          )
        )}
      </div>

      <div className="archive-stats">
        <div className="stat-item">
          <span className="stat-number">{posts.length}</span>
          <span className="stat-label">总文章数</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{Object.keys(groupedPosts).length}</span>
          <span className="stat-label">
            {viewMode === 'time' ? '年份' : '分类'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Archive;